import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-9537">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-9538">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-9539">
              <span className="text-xs font-bold text-indigo-800" data-id="element-9540">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-9541">
            <div className="text-center" data-id="element-9542">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-9543">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-9544">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-9545">→</div>
            <div className="text-center" data-id="element-9546">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-9547">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-9548">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-9549">
            <table className="w-full text-sm" data-id="element-9550">
              <thead data-id="element-9551">
                <tr className="bg-indigo-100/70" data-id="element-9552">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-9553">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-9554">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-9555">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-9556">
                      <td className="px-3 py-1.5" data-id="element-9557">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-9558">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-9559">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-9560">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-9561">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-9562">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-9563">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-9564">
            <span className="text-2xl" data-id="element-9565">📝</span>
            <div className="text-left" data-id="element-9566">
              <div className="text-sm font-bold text-indigo-900" data-id="element-9567">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-9568">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-9569">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-9570">
            <div className="text-center" data-id="element-9571">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-9572">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-9573">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-9574">→</div>
            <div className="text-center" data-id="element-9575">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-9576">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-9577">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-9578">
          <table className="w-full" data-id="element-9579">
            <thead data-id="element-9580">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-9581">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-9582">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-9583">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9584">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-9585">
                    <td className="px-4 py-3" data-id="element-9586">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-9587">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-9588">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-9589">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-9590">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-9591">
            <div className="flex items-start gap-3" data-id="element-9592">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-9593">
                <span className="text-blue-600 text-lg" data-id="element-9594">🔍</span>
              </div>
              <div data-id="element-9595">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-9596">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9597">
                  De <strong data-id="element-9598">stam</strong> krijg je door <strong data-id="element-9599">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-9600">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-9601">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-9602">
              <div className="flex items-start gap-3" data-id="element-9603">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-9604">
                  <span className="text-emerald-600 text-lg" data-id="element-9605">📋</span>
                </div>
                <div data-id="element-9606">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-9607">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-9608">
                    <p data-id="element-9609">
                      <strong data-id="element-9610">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9611">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-9612">
                      <strong data-id="element-9613">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9614">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-9615">
                      <strong data-id="element-9616">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9617">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-9618">
                      <strong data-id="element-9619">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-9620">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-9621">
            <div className="flex items-start gap-3" data-id="element-9622">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-9623">
                <span className="text-amber-600 text-lg" data-id="element-9624">💡</span>
              </div>
              <div data-id="element-9625">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-9626">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-9627">
                  Bij <strong data-id="element-9628">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-9629">jij loopt</em> wordt{' '}
                  <em data-id="element-9630">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}